import type { ReactNode } from "react";
import { cn } from "@/lib/cn";
import { Heading } from "./Heading";
import { Stack } from "./Stack";
import { Text } from "./Text";
import { ThemeIcon } from "./ThemeIcon";

export type EmptyStateTone = "default" | "filter" | "error" | "success";

const TONE_COLOR: Record<EmptyStateTone, string> = {
  default: "ink",
  filter: "ambar",
  error: "peligro",
  success: "exito",
};

export interface EmptyStateProps {
  icon?: (props: { size?: number; stroke?: number; "aria-hidden"?: boolean }) => ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
  tone?: EmptyStateTone;
  compact?: boolean;
}

/** Reemplazo de `components/ui/EmptyState.tsx` · placeholder para tablas/listas sin resultados. */
export function EmptyState({ icon: Icon, title, description, action, tone = "default", compact = false }: EmptyStateProps) {
  const color = TONE_COLOR[tone];
  return (
    <div className={cn("text-center", compact ? "py-6" : "py-10")}>
      <Stack gap={compact ? "sm" : "md"} align="center">
        {Icon && (
          <ThemeIcon size={compact ? "md" : "lg"} radius="xl" variant="light" color={color}>
            <Icon size={compact ? 22 : 28} stroke={1.6} aria-hidden />
          </ThemeIcon>
        )}
        <Stack gap={4} align="center" className="max-w-[360px]">
          <Heading order={compact ? 5 : 4}>{title}</Heading>
          {description && (
            <Text size="sm" c="dimmed" className="leading-relaxed">
              {description}
            </Text>
          )}
        </Stack>
        {action && <div className={compact ? undefined : "mt-2"}>{action}</div>}
      </Stack>
    </div>
  );
}
